import {
  ClientsInput,
  CreateCustomReportSectionItemMutation,
  CreateCustomReportSectionItemMutationVariables,
  CreateCustomReportSectionMutation,
  CreateCustomReportSectionMutationVariables,
  CustomReportResponse,
  DashboardTypes,
  DeleteCustomReportSectionItemMutation,
  DeleteCustomReportSectionItemMutationVariables,
  DeleteCustomReportSectionMutation,
  DeleteCustomReportSectionMutationVariables,
  GetCustomReportSectionsQuery,
  GetCustomReportSectionsQueryVariables,
  GetFlowchartCampaignsInDatesQuery,
  GetFlowchartCampaignsInDatesQueryVariables,
  GetPublishedOutputQuery,
  GetPublishedOutputQueryVariables,
  GridLayoutInput,
  LayoutInput,
  Placeholder,
  SectionGridLayout,
  SectionInput,
  UpdateCustomReportSectionItemMutation,
  UpdateCustomReportSectionItemMutationVariables,
  UpdateCustomReportSectionsMutation,
  UpdateCustomReportSectionsMutationVariables,
} from 'API';
import { API } from 'aws-amplify';
import {
  createCustomReportSection,
  createCustomReportSectionItem,
  deleteCustomReportSection,
  deleteCustomReportSectionItem,
  updateCustomReportSectionItem,
  updateCustomReportSections,
} from 'graphql/mutations';
import { getCustomReportSections, getFlowchartCampaignsInDates, getPublishedOutput } from 'graphql/queries';
import { useCallback } from 'react';
import { useCustomReportContext } from 'context/CustomReportContext';

export const useGetCustomReportSections = (): readonly [
  (reportID: number | undefined, section?: string) => Promise<SectionGridLayout[] | null>,
] => {
  const fetchCustomReportSections = useCallback(async (reportID: number | undefined, section?: string) => {
    if (!reportID) {
      return null;
    } else {
      try {
        const response = (await API.graphql({
          query: getCustomReportSections,
          variables: {
            CustomReportID: reportID,
            ...(section && { SectionID: section }), // Use optional chaining to include SectionID only if section is provided
            // Optionally add SectionID if needed, e.g. SectionID: section
          } as GetCustomReportSectionsQueryVariables,
          authMode: 'AMAZON_COGNITO_USER_POOLS',
        })) as { data: GetCustomReportSectionsQuery };
        const responseData = response.data.getCustomReportSections;
        if (!responseData) {
          return null;
        }
        // Ensure the return type is SectionGridLayout[] or null
        if (Array.isArray(responseData)) {
          return responseData as SectionGridLayout[];
        } else if (responseData !== null && responseData !== undefined) {
          return [responseData as SectionGridLayout];
        } else {
          return null;
        }
      } catch (e) {
        console.error(e);
        return null;
      }
    }
  }, []);
  return [fetchCustomReportSections];
};

//GET PUBLISHED DATA
export const useGetCustomReportPublishedData = (): readonly [(position?: string) => Promise<Placeholder | null>] => {
  const { activeCustomReport } = useCustomReportContext();
  const fetchPositionPublishedData = useCallback(
    async (position?: string) => {
      if (!activeCustomReport) {
        return null;
      } else {
        try {
          const response = (await API.graphql({
            query: getPublishedOutput,
            variables: {
              clientCode: activeCustomReport?.Client?.Code || '',
              clientCountry: activeCustomReport?.Client?.Country || '',
              campaign_id: activeCustomReport?.CustomReportID?.toString(),
              dashboardType: DashboardTypes.DIGITAL,

              ...(position && { position }),
            } as GetPublishedOutputQueryVariables,
            authMode: 'AMAZON_COGNITO_USER_POOLS',
          })) as { data: GetPublishedOutputQuery };
          const responseData = response.data.getPublishedOutput;
          // console.log('PUBLISHED DATA RESPONSE', responseData?.placeholders?.[0]?.metrics);
          if (!responseData) {
            return null;
          }
          if (responseData.placeholders && responseData.placeholders.length > 0) {
            // If placeholders are present, return them as SectionGridLayout[]
            return responseData.placeholders[0] as Placeholder;
          }
          return null; // Add explicit return to avoid undefined
        } catch (e) {
          console.error(e);
          return null;
        }
      }
    },
    [activeCustomReport],
  );
  return [fetchPositionPublishedData];
};

//SECTION SAVE
export const useSaveNewSection = (): readonly [
  (position: number, layout: LayoutInput, id?: number) => Promise<string>,
] => {
  const { activeCustomReport } = useCustomReportContext();
  const saveNewSection = async (position: number, layout: LayoutInput, id?: number) => {
    const idToUse = id || activeCustomReport?.CustomReportID;
    try {
      const response = (await API.graphql({
        query: createCustomReportSection,
        variables: {
          CustomReportID: idToUse,
          Position: position,
          SectionPosition: layout as LayoutInput,
        } as CreateCustomReportSectionMutationVariables,
        authMode: 'AMAZON_COGNITO_USER_POOLS',
      })) as { data: CreateCustomReportSectionMutation };
      console.log('response', response);
      return 'success';
    } catch (e) {
      console.log('ERROR SAVING CUSTOM REPORT KPI', e);
      return 'ERROR SAVING CUSTOM REPORT KPI';
    }
  };
  return [saveNewSection];
};

//ITEM SAVE
export const useSaveNewSectionItem = (): readonly [
  (
    sectionID: string,
    itemLayout: GridLayoutInput,
    customReportId?: number,
  ) => Promise<{ status: number; message: string; id?: string }>,
] => {
  const { activeCustomReport } = useCustomReportContext();
  const saveNewSectionItem = async (sectionID: string, itemLayout: GridLayoutInput, customReportId?: number) => {
    const idToUse = customReportId || activeCustomReport?.CustomReportID;
    try {
      const response = (await API.graphql({
        query: createCustomReportSectionItem,
        variables: {
          CustomReportID: idToUse,
          SectionID: sectionID,
          SectionItem: itemLayout as GridLayoutInput,
        } as CreateCustomReportSectionItemMutationVariables,
        authMode: 'AMAZON_COGNITO_USER_POOLS',
      })) as { data: CreateCustomReportSectionItemMutation };
      const res = response.data.createCustomReportSectionItem;
      if (res?.StatusCode === 200) {
        return {
          status: 200,
          message: res.Message || 'success',
          id: res.Id as string,
        };
      } else {
        return {
          status: typeof res?.StatusCode === 'number' ? res.StatusCode : 400,
          message: res?.Message || 'ERROR',
        };
      }
    } catch (e) {
      console.log('ERROR SAVING CUSTOM REPORT ITEM', e);
      return {
        status: 400,
        message: 'ERROR SAVING CUSTOM REPORT ITEM',
      };
    }
  };
  return [saveNewSectionItem];
};
//ITEM Update
export const useUpdateSectionItem = (): readonly [
  (sectionID: string, sectionItemID: string, itemLayout: GridLayoutInput) => Promise<string>,
] => {
  const { activeCustomReport } = useCustomReportContext();
  const updateSectionItem = async (sectionID: string, sectionItemID: string, itemLayout: GridLayoutInput) => {
    try {
      const response = (await API.graphql({
        query: updateCustomReportSectionItem,
        variables: {
          CustomReportID: activeCustomReport?.CustomReportID,
          SectionID: sectionID,
          SectionItemID: sectionItemID,
          SectionItem: itemLayout as GridLayoutInput,
        } as UpdateCustomReportSectionItemMutationVariables,
        authMode: 'AMAZON_COGNITO_USER_POOLS',
      })) as { data: UpdateCustomReportSectionItemMutation };
      const res = response.data.updateCustomReportSectionItem;
      if (res) {
        if (res.StatusCode === 200) {
          return 'success';
        } else {
          return res.Message as string;
        }
      }
      // Ensure a string is always returned
      return 'ERROR UPDATING CUSTOM REPORT SECTION ITEM';
    } catch (e) {
      console.log('ERROR SAVING CUSTOM REPORT KPI', e);
      return 'ERROR SAVING CUSTOM REPORT KPI';
    }
  };
  return [updateSectionItem];
};

//update sections
export const useUpdateSection = (): readonly [(sections: SectionInput[]) => Promise<string>] => {
  const { activeCustomReport } = useCustomReportContext();
  const updateSection = async (sections: SectionInput[]) => {
    try {
      const response = (await API.graphql({
        query: updateCustomReportSections,
        variables: {
          CustomReportID: activeCustomReport?.CustomReportID,
          Sections: sections as SectionInput[],
        } as UpdateCustomReportSectionsMutationVariables,
        authMode: 'AMAZON_COGNITO_USER_POOLS',
      })) as { data: UpdateCustomReportSectionsMutation };
      const res = response.data.updateCustomReportSections;
      if (res) {
        if (res.StatusCode === 200) {
          return 'success';
        } else {
          return res.Message as string;
        }
      }
      // Ensure a string is always returned
      return 'ERROR UPDATING CUSTOM REPORT SECTION ITEM';
    } catch (e) {
      console.log('ERROR SAVING CUSTOM REPORT KPI', e);
      return 'ERROR SAVING CUSTOM REPORT KPI';
    }
  };
  return [updateSection];
};

//ITEM DELETE
export const useDeleteSectionItem = (): readonly [(sectionID: string, sectionItemID: string) => Promise<string>] => {
  const { activeCustomReport } = useCustomReportContext();
  const deleteSectionItem = async (sectionID: string, sectionItemID: string) => {
    try {
      const response = (await API.graphql({
        query: deleteCustomReportSectionItem,
        variables: {
          CustomReportID: activeCustomReport?.CustomReportID,
          SectionID: sectionID,
          SectionItemID: sectionItemID,
        } as DeleteCustomReportSectionItemMutationVariables,
        authMode: 'AMAZON_COGNITO_USER_POOLS',
      })) as { data: DeleteCustomReportSectionItemMutation };
      const res = response.data.deleteCustomReportSectionItem;
      if (res) {
        if (res.StatusCode === 200) {
          return 'success';
        } else {
          return res.Message as string;
        }
      }
      return 'ERROR DELETING CUSTOM REPORT SECTION ITEM';
    } catch (e) {
      console.log('ERROR DELETING CUSTOM REPORT SECTION ITEM', e);
      return 'ERROR DELETING CUSTOM REPORT SECTION ITEM';
    }
  };
  return [deleteSectionItem];
};

//Section DELETE
export const useDeleteSection = (): readonly [(sectionID: string) => Promise<string>] => {
  const { activeCustomReport } = useCustomReportContext();
  const deleteSection = async (sectionID: string) => {
    try {
      const response = (await API.graphql({
        query: deleteCustomReportSection,
        variables: {
          CustomReportID: activeCustomReport?.CustomReportID,
          SectionID: sectionID,
        } as DeleteCustomReportSectionMutationVariables,
        authMode: 'AMAZON_COGNITO_USER_POOLS',
      })) as { data: DeleteCustomReportSectionMutation };
      const res = response.data.deleteCustomReportSection;
      if (res) {
        if (res.StatusCode === 200) {
          return 'success';
        } else {
          return res.Message as string;
        }
      }
      return 'ERROR DELETING CUSTOM REPORT SECTION ITEM';
    } catch (e) {
      console.log('ERROR DELETING CUSTOM REPORT SECTION ITEM', e);
      return 'ERROR DELETING CUSTOM REPORT SECTION ITEM';
    }
  };
  return [deleteSection];
};

//GET DATA SOURCES
// export const useCustomReportsGetDataSources = (): readonly [
//   (reportID: number | undefined, campaigns: string[]) => Promise<DataSourcesInfo[] | null>,
// ] => {
//   const fetchDataSources = useCallback(async (reportID: number | undefined, campaigns: string[]) => {
//     if (!reportID) {
//       return null;
//     } else {
//       try {
//         const response = (await API.graphql({
//           query: getDataSourcesV2,
//           variables: {
//             campaignIds: campaigns,
//             // campaignIds: ['17777', '1000005', '17804'],
//           } as GetDataSourcesV2QueryVariables,
//           authMode: 'AMAZON_COGNITO_USER_POOLS',
//         })) as { data: GetDataSourcesV2Query };
//         const responseData = response.data.getDataSourcesV2;
//         if (!responseData) {
//           return null;
//         }
//         // Ensure the return type is DataSourcesInfo[] or null
//         if (Array.isArray(responseData)) {
//           return responseData as DataSourcesInfo[];
//         } else if (responseData !== null && responseData !== undefined) {
//           return [responseData as DataSourcesInfo];
//         } else {
//           return null;
//         }
//       } catch (e) {
//         console.error(e);
//         return null;
//       }
//     }
//   }, []);
//   return [fetchDataSources];
// };

export const useDateReportsCampaigns = (): readonly [(report: CustomReportResponse) => Promise<string[] | null>] => {
  const fetchDateReportsCampaigns = useCallback(async (report: CustomReportResponse) => {
    if (!report) {
      return null;
    } else {
      const clientCodes = [
        {
          ClientCode: report.Client?.Code,
          ClientCountry: report.Client?.Country,
        } as ClientsInput,
      ];
      const from = report.Filters?.[0]?.filter?.from as string;
      const to = report.Filters?.[0]?.filter?.to as string;

      try {
        const response = (await API.graphql({
          query: getFlowchartCampaignsInDates,
          variables: {
            clientCodes: clientCodes,
            from: from,
            to: to,
          } as GetFlowchartCampaignsInDatesQueryVariables,
          authMode: 'AMAZON_COGNITO_USER_POOLS',
        })) as { data: GetFlowchartCampaignsInDatesQuery };
        const responseData = response.data.getFlowchartCampaignsInDates;
        if (!responseData) {
          return null;
        }
        if (responseData) {
          const campaigns = responseData
            .map((item) => item?.campaignId.toString())
            .filter((id) => id !== null && id !== undefined);
          return campaigns as string[];
        } else {
          return null;
        }
      } catch (e) {
        console.error(e);
        return null;
      }
    }
  }, []);
  return [fetchDateReportsCampaigns];
};
